import React, { useRef, useEffect } from "react";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link2,
  User,
  Eraser,
  MousePointerClick,
} from "lucide-react";

const ToolBtn = ({ onClick, title, testId, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testId}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="w-8 h-8 flex items-center justify-center rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
  >
    {children}
  </button>
);

/**
 * Lightweight Word-like WYSIWYG editor (zero deps, React 19 safe).
 * Produces HTML via `onChange`. Formatting is preserved exactly on send.
 */
export const RichTextEditor = ({ value, onChange, placeholder, testId }) => {
  const ref = useRef(null);
  const savedRange = useRef(null);

  // Initialise content once (uncontrolled to keep the caret stable).
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange?.(ref.current?.innerHTML || "");

  // Remember the current text selection so toolbar controls that steal focus
  // (like the native <select>) can restore it before applying a command.
  const saveSel = () => {
    const s = window.getSelection();
    if (s && s.rangeCount && ref.current && ref.current.contains(s.anchorNode)) {
      savedRange.current = s.getRangeAt(0).cloneRange();
    }
  };

  const restoreSel = () => {
    if (savedRange.current) {
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(savedRange.current);
    }
  };

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  // Apply an exact font size (px) to the selection, keeping Arial.
  const setSize = (px) => {
    if (!px) return;
    ref.current?.focus();
    restoreSel();
    document.execCommand("fontSize", false, "7");
    ref.current.querySelectorAll('font[size="7"]').forEach((f) => {
      f.removeAttribute("size");
      f.style.fontSize = `${px}px`;
      f.style.lineHeight = "1.5";
    });
    emit();
  };

  const insertMerge = () => {
    ref.current?.focus();
    document.execCommand("insertText", false, "{{nome}}");
    emit();
  };

  const makeLink = () => {
    const url = window.prompt("Inserisci l'URL del link (https://...)");
    if (url) exec("createLink", url.trim());
  };

  const insertButton = () => {
    const label = window.prompt("Testo del pulsante (es. Prenota la call)", "Scopri di più");
    if (!label) return;
    let url = window.prompt("Link del pulsante (https://...)", "https://mydigitalaction.it");
    if (!url) return;
    url = url.trim();
    if (!/^https?:\/\//i.test(url)) url = "https://" + url;
    const safe = label.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    ref.current?.focus();
    const block = document.createElement("div");
    block.style.margin = "18px 0";
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.setAttribute(
      "style",
      "display:inline-block;background:#d4ff3d;color:#0a0a0a;text-decoration:none;" +
        "padding:13px 26px;border-radius:999px;font-weight:700;" +
        "font-family:Arial,Helvetica,sans-serif;font-size:15px;"
    );
    a.innerHTML = safe + " &rarr;";
    block.appendChild(a);
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      range.collapse(false);
      range.insertNode(block);
      range.setStartAfter(block);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      ref.current.appendChild(block);
    }
    emit();
  };

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 bg-[#141414] border-b border-white/10">
        <ToolBtn onClick={() => exec("bold")} title="Grassetto" testId="rte-bold"><Bold size={15} /></ToolBtn>
        <ToolBtn onClick={() => exec("italic")} title="Corsivo" testId="rte-italic"><Italic size={15} /></ToolBtn>
        <ToolBtn onClick={() => exec("underline")} title="Sottolineato" testId="rte-underline"><Underline size={15} /></ToolBtn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <select
          onChange={(e) => { setSize(e.target.value); e.target.selectedIndex = 0; }}
          title="Dimensione testo"
          data-testid="rte-fontsize"
          defaultValue=""
          className="h-8 bg-[#1f1f1f] border border-white/15 rounded-md text-white/80 text-xs px-2 focus:outline-none cursor-pointer"
        >
          <option value="" disabled>Dimensione</option>
          <option value="14">14 — Piccolo</option>
          <option value="16">16 — Normale</option>
          <option value="18">18</option>
          <option value="20">20</option>
          <option value="24">24 — Titolo</option>
          <option value="28">28 — Titolo grande</option>
          <option value="32">32 — Titolone</option>
        </select>
        <ToolBtn onClick={() => exec("insertUnorderedList")} title="Elenco puntato" testId="rte-ul"><List size={15} /></ToolBtn>
        <ToolBtn onClick={() => exec("insertOrderedList")} title="Elenco numerato" testId="rte-ol"><ListOrdered size={15} /></ToolBtn>
        <ToolBtn onClick={makeLink} title="Inserisci link" testId="rte-link"><Link2 size={15} /></ToolBtn>
        <ToolBtn onClick={insertButton} title="Inserisci pulsante (CTA)" testId="rte-cta"><MousePointerClick size={15} /></ToolBtn>
        <ToolBtn onClick={() => exec("removeFormat")} title="Rimuovi formattazione" testId="rte-clear"><Eraser size={15} /></ToolBtn>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={insertMerge}
          data-testid="rte-insert-name"
          className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 h-7 rounded-md"
          style={{ background: "#d4ff3d", color: "#0a0a0a" }}
          title="Inserisce il nome del contatto automaticamente"
        >
          <User size={13} /> Inserisci nome
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onKeyUp={saveSel}
        onMouseUp={saveSel}
        data-placeholder={placeholder}
        data-testid={testId}
        className="rte-content min-h-[240px] max-h-[460px] overflow-auto px-4 py-3 text-[16px] leading-relaxed text-[#1a1a1a] focus:outline-none"
        style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
      />
    </div>
  );
};

export default RichTextEditor;
